import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Plus, Trash2, Undo2, Redo2, Scissors, Copy, ArrowLeftToLine, ArrowRightToLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { AddLayerContextMenuRows } from '@/components/layers/AddLayerContextMenuRows';
import { useVideoFrameExtractor } from '@/hooks/use-video-frame-extractor';
import { useTranslation } from 'react-i18next';
import CropCenterTrack from '@/components/CropCenterTrack';
import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import AddLayerMenu from '@/components/layers/AddLayerMenu';
import LayerLanes from '@/components/layers/LayerLanes';
import { EffectLanes } from '@/components/video-effects/EffectLanes';
import { EffectsLibraryButton } from '@/components/video-effects/EffectsLibraryButton';
import { EFFECT_LANE_HEIGHT, effectLaneRowCount, sectionsWithinClip } from '@/lib/video-effects/sectionTimeline';
import type { EffectId, EffectSection } from '@/lib/video-effects/sections';
import type { AddableLayerType } from '@/lib/layers/layerTypeConfig';
import { removeInterval } from '@/lib/layers/removeInterval';
import { TransitionBadge } from '@/components/transitions/TransitionBadge';
import type { SceneTransition, TransitionKind } from '@/lib/transitions/types';
import { applyIntervalDrag } from '@/lib/timeline/applyIntervalDrag';
import { usePlayheadInit } from '@/hooks/use-playhead-init';
import { resolvePlayheadEdge } from '@/lib/timeline/resolvePlayheadEdge';
import { planFootageInsertion } from '@/lib/timeline/planFootageInsertion';
import { getVisibleScenePreviews, MIN_SCENE_FRAME_WIDTH } from '@/lib/timeline/getVisibleScenePreviews';
import { TimelineScenePreviews } from '@/components/TimelineScenePreviews';
import { buildSceneLayout } from '@/lib/transitions/buildSceneLayout';
import { outputFrameToSourceFrame, sourceTimeToOutputTime } from '@/lib/transitions/mapTimes';
import { reorderSceneAtTime } from '@/lib/timeline/naturalScenes';
import { fillIntervalCropGaps } from '@/lib/crop/fillIntervalCropGaps';

import { EXPORT_FPS, FRAME_DURATION, snapToExportFrame, lastCompleteFrameTime, formatFrameTime } from '@/lib/timeline/exportFrameTiming';

const EMPTY_THUMBNAIL_FRAMES = new Map<number, string>();

type TimelineInterval = {
  id: number;
  startTime: number;
  endTime: number;
  title?: string;
};

interface TimelineProps {
  fullVideoDuration: number; // Total duration of the full video in seconds
  selectedClip?: {
    id: number;
    startTime: number;
    endTime: number;
    title: string;
  } | null;
  intervals?: Array<{
    id: number;
    startTime: number;
    endTime: number;
    title?: string;
  }>;
  onTimeChange?: (time: number) => void;
  onSeekRequest?: (time: number, opts?: { scrub?: boolean }) => void;
  currentTime?: number;
  className?: string;
  onIntervalsChange?: (intervals: Array<{ id: number; startTime: number; endTime: number; title?: string }>) => void;
  isVideoLocked?: boolean;
  onVideoLockedChange?: (locked: boolean) => void;
  /** Place scenes in playback order; body drags reorder rather than slip source footage. */
  sequenceMode?: boolean;
  sceneMarkers?: Array<{ startTime: number; endTime: number }>;
  cropCenterKeyframes?: CropCenterKeyframe[];
  /** Offer framing on scenes no crop keyframe covers, starting from the crop
   *  the preview holds there. Only when the preview applies crop keyframes. */
  cropUncoveredScenes?: boolean;
  onCropKeyframesChange?: (next: CropCenterKeyframe[]) => void;
  sourceAspectRatio?: number;
  videoUrl?: string;
  thumbnailVideoUrl?: string;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  layers?: import('@/lib/layers/types').MediaLayer[];
  activeLayerId?: string | null;
  onLayerSelect?: (id: string | null) => void;
  onLayerPatch?: (id: string, patch: Partial<import('@/lib/layers/types').MediaLayer>) => void;
  /** Restack a layer: move it to `toIndex` in the layers array (array order is z-order). */
  onLayerReorder?: (id: string, toIndex: number) => void;
  clipDuration?: number;
  /** Timeline effect sections (clip output time) drawn in lanes below the layer lanes. */
  effectSections?: EffectSection[];
  activeEffectSectionId?: string | null;
  onEffectSectionSelect?: (id: string | null) => void;
  onEffectSectionCommit?: (id: string, window: { start: number; end: number }) => void;
  onAddEffect?: (effect: EffectId) => void;
  onAddLayer?: (type: AddableLayerType) => void;
  /** Currently selected scene/interval id (for keyboard delete + highlight). */
  selectedIntervalId?: number | null;
  onSelectInterval?: (id: number | null) => void;
  transitions?: SceneTransition[];
  /** Set/replace (transition non-null) or remove (null) the transition at a cut. */
  onTransitionChange?: (cutIndex: number, transition: SceneTransition | null) => void;
  /** Copy one cut's transition to every cut. */
  onApplyTransitionToAll?: (transition: SceneTransition) => void;
  /** Assign-at-click randomize from a preset subset. */
  onRandomizeTransitions?: (kinds: TransitionKind[]) => void;
  /** Assign-at-click signal-driven generator (video_signals_url). Omitted =>
   *  the Auto affordance is not rendered at all. */
  onAutoTransitions?: () => void;
  /** Non-null disables Auto and explains why (e.g. "this project has no
   *  signals"). Presented as a note, not an error. */
  autoTransitionsDisabledReason?: string | null;
  /** Unobtrusive "Detected: <content type>" label. */
  autoTransitionsContentTypeLabel?: string | null;
  /** Per-crop-keyframe-boundary transitions (transform family only). */
  keyframeTransitions?: import('@/lib/transitions/types').KeyframeTransition[];
  onKeyframeTransitionChange?: (atTime: number, transition: SceneTransition | null) => void;
  onApplyKeyframeTransitionToAll?: (transition: SceneTransition) => void;
  onRandomizeKeyframeTransitions?: (kinds: TransitionKind[]) => void;
  /** Skip filmstrip seeks while the preview is buffering or playing. */
  suspendFrameExtraction?: boolean;
  /** Extra clip-scoped controls rendered at the end of the left toolbar group
   *  (the editor puts the clip cover control here). */
  toolbarStart?: React.ReactNode;
  /** False while `intervals` still belong to the previously selected clip
   *  (see usePlayheadInit). */
  intervalsLoaded?: boolean;
}

const Timeline: React.FC<TimelineProps> = ({
  fullVideoDuration,
  selectedClip,
  intervals = [],
  intervalsLoaded = true,
  onTimeChange,
    onSeekRequest,
  currentTime = 0,
  className = '',
  onIntervalsChange,
  isVideoLocked = false,
  onVideoLockedChange,
  sequenceMode = false,
  sceneMarkers,
  cropCenterKeyframes,
  cropUncoveredScenes = false,
  onCropKeyframesChange,
  sourceAspectRatio,
  videoUrl,
  thumbnailVideoUrl,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  layers,
  activeLayerId,
  onLayerSelect,
  onLayerPatch,
  onLayerReorder,
  clipDuration,
  effectSections,
  activeEffectSectionId,
  onEffectSectionSelect,
  onEffectSectionCommit,
  onAddEffect,
  onAddLayer,
  selectedIntervalId,
  onSelectInterval,
  transitions,
  onTransitionChange,
  onApplyTransitionToAll,
  onRandomizeTransitions,
  onAutoTransitions,
  autoTransitionsDisabledReason = null,
  autoTransitionsContentTypeLabel = null,
  keyframeTransitions,
  onKeyframeTransitionChange,
  onApplyKeyframeTransitionToAll,
  onRandomizeKeyframeTransitions,
  suspendFrameExtraction,
  toolbarStart = false,
}) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef<boolean>(false);
  const dragStateRef = useRef<{
    intervalId: number;
    mode: 'start' | 'end' | 'move' | 'reorder';
    original: TimelineInterval[];
    centers: number[];
    initialCenter: number;
    secondsPerPixel: number;
    moved: boolean;
    startClientX: number;
    initialStart: number;
    initialEnd: number;
  } | null>(null);
  const isDraggingTimeRef = useRef<boolean>(false);
  const suppressClickRef = useRef(false);
  const timeIndicatorDragRef = useRef<{
    startClientX: number;
    initialTime: number;
  } | null>(null);
  const pendingTimeRef = useRef<number | null>(null);
  const timeRafRef = useRef<number | null>(null);
  const seekingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearSeekingSoon = useCallback(() => {
    if (seekingTimeoutRef.current != null) {
      clearTimeout(seekingTimeoutRef.current);
    }
    seekingTimeoutRef.current = setTimeout(() => {
      seekingTimeoutRef.current = null;
      setIsSeeking(false);
    }, 200);
  }, []);
  // Live bar positions during a gesture. Committed to the parent only on
  // pointerup so ProjectEditor / Remotion do not remount every pixel.
  const [draftIntervals, setDraftIntervals] = useState<TimelineInterval[] | null>(null);
  const draftIntervalsRef = useRef<TimelineInterval[] | null>(null);
  const intervalsRef = useRef(intervals);
  const onIntervalsChangeRef = useRef(onIntervalsChange);
  const onTimeChangeRef = useRef(onTimeChange);
  const onSeekRequestRef = useRef(onSeekRequest);
  intervalsRef.current = intervals;
  onIntervalsChangeRef.current = onIntervalsChange;
  onTimeChangeRef.current = onTimeChange;
  onSeekRequestRef.current = onSeekRequest;
  const displayIntervals = draftIntervals ?? intervals;

  const frameVideoUrl = thumbnailVideoUrl ?? videoUrl;
  const thumbnailAspect = sourceAspectRatio && sourceAspectRatio > 0 ? sourceAspectRatio : 16 / 9;
  const { extractFrame } = useVideoFrameExtractor(frameVideoUrl, {
    canvasWidth: Math.round(128 * Math.min(1, thumbnailAspect)),
    canvasHeight: Math.round(128 / Math.max(1, thumbnailAspect)),
    quality: 0.55, maxCachedFrames: 80,
  });
  const [thumbnailCache, setThumbnailCache] = useState({
    videoUrl: frameVideoUrl,
    frames: new Map<number, string>(),
  });
  const thumbnailFrames = thumbnailCache.videoUrl === frameVideoUrl
    ? thumbnailCache.frames
    : EMPTY_THUMBNAIL_FRAMES;
  const frameCacheRef = useRef(thumbnailCache);
  const [viewportWidth, setViewportWidth] = useState(0);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const updateWidth = () => setViewportWidth(container.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const renderIntervals = React.useMemo(
    () => displayIntervals.length ? displayIntervals : selectedClip ? [selectedClip] : [],
    [displayIntervals, selectedClip],
  );
  const isSequence = sequenceMode && renderIntervals.length > 0;
  // A scene no keyframe covers (e.g. auto-crop stopped before the clip) gets
  // the crop the preview holds there as explicit keyframes, so it can be
  // reframed without moving the other uncovered scenes.
  const sceneCropKeyframes = React.useMemo(
    () => (cropUncoveredScenes && cropCenterKeyframes
      ? fillIntervalCropGaps(cropCenterKeyframes, renderIntervals)
      : cropCenterKeyframes),
    [cropUncoveredScenes, cropCenterKeyframes, renderIntervals],
  );
  const sequenceLayout = React.useMemo(() => buildSceneLayout({
    scenes: renderIntervals, transitions: transitions ?? [], fps: EXPORT_FPS,
  }), [renderIntervals, transitions]);
  const timelineDuration = isSequence ? sequenceLayout.totalDurationInFrames / EXPORT_FPS : fullVideoDuration;
  const timelineWidth = Math.max(timelineDuration * zoom * 10, viewportWidth);
  const pixelsPerSecond = timelineDuration > 0 ? timelineWidth / timelineDuration : 0;
  const axisRanges = React.useMemo(() => renderIntervals.map((clip, index) => ({
    clip,
    start: isSequence ? sequenceLayout.blocks[index].from / EXPORT_FPS : clip.startTime,
    end: isSequence ? (sequenceLayout.blocks[index].from + sequenceLayout.blocks[index].durationInFrames) / EXPORT_FPS : clip.endTime,
  })), [renderIntervals, isSequence, sequenceLayout]);
  const axisCurrentTime = isSequence
    ? sourceTimeToOutputTime(renderIntervals, sequenceLayout.blocks, currentTime, EXPORT_FPS)
    : currentTime;
  const sourceTimeAtAxis = useCallback((time: number) => isSequence
    ? outputFrameToSourceFrame(sequenceLayout.blocks, Math.round(time * EXPORT_FPS)) / EXPORT_FPS
    : Math.max(0, Math.min(lastCompleteFrameTime(fullVideoDuration), snapToExportFrame(time))), [isSequence, sequenceLayout, fullVideoDuration]);
  const axisRef = useRef({ duration: timelineDuration, sourceTimeAtAxis });
  axisRef.current = { duration: timelineDuration, sourceTimeAtAxis };
  const visibleScenePreviews = React.useMemo(() => {
    if (!pixelsPerSecond) return [];
    const viewStart = Math.max(0, scrollPosition) / pixelsPerSecond;
    const viewEnd = (scrollPosition + (viewportWidth || timelineWidth)) / pixelsPerSecond;
    return axisRanges.flatMap(({ clip, start }) => getVisibleScenePreviews({
      intervals: [clip], sceneMarkers: sceneMarkers?.length ? sceneMarkers : [clip],
      viewStart: clip.startTime + viewStart - start,
      viewEnd: clip.startTime + viewEnd - start,
    }));
  }, [axisRanges, sceneMarkers, scrollPosition, viewportWidth, timelineWidth, pixelsPerSecond]);

  useEffect(() => {
    if (frameCacheRef.current.videoUrl !== frameVideoUrl) {
      frameCacheRef.current = { videoUrl: frameVideoUrl, frames: new Map() };
      setThumbnailCache(frameCacheRef.current);
    }
    if (!frameVideoUrl || suspendFrameExtraction) return;

    let cancelled = false;
    const loadFrames = async () => {
      const requested = new Set<number>();
      for (const preview of visibleScenePreviews) {
        if (cancelled) break;
        if ((preview.visibleEndTime - preview.visibleStartTime) * pixelsPerSecond < MIN_SCENE_FRAME_WIDTH) continue;
        const roundedTime = Math.round(preview.startTime * 10) / 10;
        if (frameCacheRef.current.frames.has(roundedTime) || requested.has(roundedTime)) continue;
        requested.add(roundedTime);

        const frame = await extractFrame(preview.startTime);
        if (cancelled) break;
        if (frame) {
          const frames = new Map(frameCacheRef.current.frames);
          frames.set(roundedTime, frame);
          while (frames.size > 80) frames.delete(frames.keys().next().value!);
          frameCacheRef.current = { videoUrl: frameVideoUrl, frames };
          setThumbnailCache(frameCacheRef.current);
        }
      }
    };
    void loadFrames();
    return () => { cancelled = true; };
  }, [visibleScenePreviews, extractFrame, frameVideoUrl, pixelsPerSecond, suspendFrameExtraction]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.5, 10));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.5, 0.1));
  };

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.5;
      const newScrollPosition = direction === 'left'
        ? Math.max(0, scrollPosition - scrollAmount)
        : Math.min(container.scrollWidth - container.clientWidth, scrollPosition + scrollAmount);

      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
      setScrollPosition(newScrollPosition);
    }
  };

  /** Move the playhead to a source-video time, the same way a timeline click
   *  does: optimistic local update plus a seek request to the player. */
  const seekTo = useCallback((videoTime: number) => {
    const clamped = Math.max(0, Math.min(snapToExportFrame(videoTime), lastCompleteFrameTime(fullVideoDuration)));
    setIsSeeking(true);
    onTimeChange?.(clamped);
    onSeekRequest?.(clamped);
    clearSeekingSoon();
  }, [fullVideoDuration, onTimeChange, onSeekRequest, clearSeekingSoon]);

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current) return;

    // Don't handle clicks if we're dragging
    if (isDraggingRef.current || isDraggingTimeRef.current) return;
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }

    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const timelineWidth = rect.width;
    const clickedTime = sourceTimeAtAxis((clickX / timelineWidth) * timelineDuration);

    const clamped = Math.max(0, Math.min(clickedTime, fullVideoDuration));

    // Set seeking state for visual feedback
    setIsSeeking(true);

    // Update time immediately for responsive UI
    onTimeChange?.(clamped);

    // Request seek from parent component
    onSeekRequest?.(clamped);

    // Clear seeking state after a short delay
    clearSeekingSoon();
  };

  const getCurrentTimePosition = () => {
    return timelineDuration > 0 ? (axisCurrentTime / timelineDuration) * 100 : 0;
  };
  // The playhead head (≤20px) and its time label (~60px) are centered on the
  // line, except at the track ends, where the scroll container would clip them.
  const playheadPx = (getCurrentTimePosition() / 100) * timelineWidth;
  const playheadHeadEdge = resolvePlayheadEdge({ positionPx: playheadPx, trackWidthPx: timelineWidth, halfWidthPx: 10 });
  const playheadLabelEdge = resolvePlayheadEdge({ positionPx: playheadPx, trackWidthPx: timelineWidth, halfWidthPx: 32 });

  const centerOnCurrentTime = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (timelineDuration <= 0) return;

    const timelinePixelWidth = timelineWidth;
    if (!Number.isFinite(timelinePixelWidth) || timelinePixelWidth <= 0) return;

    const indicatorPx = (axisCurrentTime / timelineDuration) * timelinePixelWidth;
    const targetLeft = Math.max(
      0,
      Math.min(
        indicatorPx - container.clientWidth / 2,
        container.scrollWidth - container.clientWidth
      )
    );

    container.scrollTo({ left: targetLeft, behavior: 'auto' });
    setScrollPosition(targetLeft);
  }, [axisCurrentTime, timelineDuration, timelineWidth]);

  useEffect(() => {
    // Keep the current time indicator centered whenever zoom changes
    // but only if we're not currently dragging anything
    if (!isDraggingRef.current && !isDraggingTimeRef.current) {
      centerOnCurrentTime();
    }
  }, [zoom, centerOnCurrentTime]);

  // Track native scroll for viewport-aware thumbnail loading. rAF-coalesce
  // so a fling does not re-render Timeline + extract thumbnails every pixel.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let raf: number | null = null;
    const onScroll = () => {
      if (raf != null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        setScrollPosition(container.scrollLeft);
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  usePlayheadInit({ intervals, selectedClip, onTimeChange, onSeekRequest, intervalsLoaded });

  // Parent replaced the committed intervals (clip switch, undo, add/split).
  // Drop any in-flight draft so we do not paint stale bars.
  useEffect(() => {
    draftIntervalsRef.current = null;
    setDraftIntervals(null);
  }, [intervals]);

  const [addChoiceOpen, setAddChoiceOpen] = useState(false);

  // Source location is independent of playback order. Append new footage
  // without undoing the user's existing scene arrangement.
  const appendFootage = (startTime: number, endTime: number) => {
    if (!onIntervalsChange) return;
    onIntervalsChange([
      ...renderIntervals,
      {
        id: Math.max(Date.now(), ...renderIntervals.map(interval => interval.id + 1)),
        startTime,
        endTime,
        title: 'Intervalo',
      },
    ]);
  };

  // Explicit copy of up to 5 s of the footage under the playhead, even when a
  // scene already keeps it (a replay).
  const handleAddCopy = () => {
    const sourceLimit = lastCompleteFrameTime(fullVideoDuration);
    const startTime = Math.max(0, Math.min(snapToExportFrame(currentTime), sourceLimit));
    const endTime = snapToExportFrame(Math.min(startTime + 5, sourceLimit));
    if (endTime - startTime < FRAME_DURATION - 1e-7) return;
    appendFootage(startTime, endTime);
  };

  const handleAddInterval = () => {
    if (!onIntervalsChange) {
      return; // Parent is not controlling intervals
    }

    const sourceLimit = lastCompleteFrameTime(fullVideoDuration);
    const insertionTime = Math.max(0, Math.min(snapToExportFrame(currentTime), sourceLimit));
    // When video is unlocked, create the interval at the current time — but
    // inside a kept scene that would only copy its footage, so offer a split.
    if (!isVideoLocked) {
      const plan = planFootageInsertion({ intervals: renderIntervals, time: currentTime, sourceLimit });
      if (plan.kind === 'inside-scene') {
        setAddChoiceOpen(true);
        return;
      }
      if (plan.kind === 'add') appendFootage(plan.startTime, plan.endTime);
      return;
    }

    // When video is locked, use original logic
    const baseIntervals: Array<{ id: number; startTime: number; endTime: number; title?: string }> =
      intervals && intervals.length > 0
        ? intervals
        : (selectedClip
            ? [{ id: selectedClip.id, startTime: selectedClip.startTime, endTime: selectedClip.endTime, title: selectedClip.title }]
            : []);

    if (baseIntervals.length === 0) {
      // Create a default interval starting at current time
      const defaultDuration = Math.min(5, Math.max(0, sourceLimit - insertionTime));
      if (defaultDuration <= 0) return;
      const newInterval = {
        id: Math.max(Date.now(), ...renderIntervals.map(interval => interval.id + 1)),
        startTime: insertionTime,
        endTime: snapToExportFrame(insertionTime + defaultDuration),
        title: 'Intervalo'
      };
      onIntervalsChange([newInterval]);
      return;
    }

    // Find the current interval (containing currentTime) or the last before currentTime
    let currentIndex = baseIntervals.findIndex((it) => currentTime >= it.startTime && currentTime <= it.endTime);
    if (currentIndex === -1) {
      // find the last interval that starts before current time, else use last
      const before = baseIntervals
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => it.startTime <= currentTime)
        .sort((a, b) => b.it.startTime - a.it.startTime);
      currentIndex = before.length > 0 ? before[0].idx : baseIntervals.length - 1;
    }

    const currentInterval = baseIntervals[currentIndex];
    const currentDuration = Math.max(FRAME_DURATION, snapToExportFrame(currentInterval.endTime) - snapToExportFrame(currentInterval.startTime));
    const newStart = Math.min(sourceLimit, snapToExportFrame(currentInterval.endTime));
    const newEnd = Math.min(sourceLimit, snapToExportFrame(newStart + currentDuration));
    if (newStart >= sourceLimit) return;

    const newInterval = {
      id: Math.max(Date.now(), ...renderIntervals.map(interval => interval.id + 1)),
      startTime: newStart,
      endTime: newEnd,
      title: currentInterval.title || 'Intervalo'
    };

    const newList = [
      ...baseIntervals.slice(0, currentIndex + 1),
      newInterval,
      ...baseIntervals.slice(currentIndex + 1)
    ];
    onIntervalsChange(newList);
  };

  // Split the interval currently containing the playhead at currentTime,
  // leaving two adjacent intervals. No-op when the playhead is not inside
  // an interval or sits too close to one of its edges.
  const [contextTime, setContextTime] = useState<number | null>(null);
  const [contextIntervalId, setContextIntervalId] = useState<number | null>(null);
  const contextIntervalIndex = renderIntervals.findIndex(interval => interval.id === contextIntervalId);
  const moveContextScene = (edge: 'start' | 'end') => {
    if (!onIntervalsChange || contextIntervalIndex < 0) return;
    const to = edge === 'start' ? 0 : renderIntervals.length - 1;
    if (contextIntervalIndex === to) return;
    const next = [...renderIntervals];
    const [scene] = next.splice(contextIntervalIndex, 1);
    next.splice(to, 0, scene);
    onIntervalsChange(next);
  };
  const splitTime = snapToExportFrame(contextTime ?? currentTime);
  const splitHostIndex = React.useMemo(() => {
    const base =
      displayIntervals && displayIntervals.length > 0
        ? displayIntervals
        : selectedClip
          ? [
              {
                id: selectedClip.id,
                startTime: selectedClip.startTime,
                endTime: selectedClip.endTime,
                title: selectedClip.title,
              },
            ]
          : [];
    return base.findIndex(
      (it) =>
        splitTime >= snapToExportFrame(it.startTime) + FRAME_DURATION - 1e-7 &&
        splitTime <= snapToExportFrame(it.endTime) - FRAME_DURATION + 1e-7,
    );
  }, [displayIntervals, selectedClip, splitTime]);

  const canSplitAtPlayhead = splitHostIndex >= 0 && !!onIntervalsChange;
  // Unlocked "+" at the playhead would copy kept footage (see handleAddInterval).
  const playheadInsideScene = !isVideoLocked && planFootageInsertion({
    intervals: renderIntervals,
    time: currentTime,
    sourceLimit: lastCompleteFrameTime(fullVideoDuration),
  }).kind === 'inside-scene';

  const handleSplitAtPlayhead = () => {
    if (!onIntervalsChange) return;
    const base: Array<{ id: number; startTime: number; endTime: number; title?: string }> =
      intervals && intervals.length > 0
        ? intervals
        : selectedClip
          ? [
              {
                id: selectedClip.id,
                startTime: selectedClip.startTime,
                endTime: selectedClip.endTime,
                title: selectedClip.title,
              },
            ]
          : [];
    const idx = base.findIndex(
      (it) =>
        splitTime >= snapToExportFrame(it.startTime) + FRAME_DURATION - 1e-7 &&
        splitTime <= snapToExportFrame(it.endTime) - FRAME_DURATION + 1e-7,
    );
    if (idx < 0) return;
    const original = base[idx];
    const next = [...base];
    next[idx] = { ...original, endTime: splitTime };
    next.splice(idx + 1, 0, {
      ...original,
      id: Math.max(Date.now(), ...renderIntervals.map(interval => interval.id + 1)),
      startTime: splitTime,
      endTime: original.endTime,
    });
    onIntervalsChange(next);
  };

  const handleDeleteInterval = (intervalId: number) => {
    if (!onIntervalsChange) return;
    const base: Array<{ id: number; startTime: number; endTime: number; title?: string }> =
      intervals && intervals.length > 0
        ? intervals
        : (selectedClip
            ? [{ id: selectedClip.id, startTime: selectedClip.startTime, endTime: selectedClip.endTime, title: selectedClip.title }]
            : []);

    // removeInterval refuses to drop the last remaining segment.
    const filtered = removeInterval(base, intervalId);
    if (filtered === base) return;
    onSelectInterval?.(null);
    onIntervalsChange(filtered);
  };

  const beginIntervalGesture = (intervalId: number, mode: 'start' | 'end' | 'move' | 'reorder', e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (!onIntervalsChange || !intervals.length) return;
    const target = intervals.find((i) => i.id === intervalId);
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!target || !rect?.width) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    isDraggingRef.current = true;
    suppressClickRef.current = false;
    const centers = axisRanges.map(range => (range.start + range.end) / 2);
    dragStateRef.current = {
      intervalId, mode, original: intervals, centers,
      initialCenter: centers[intervals.findIndex(i => i.id === intervalId)],
      secondsPerPixel: timelineDuration / rect.width,
      startClientX: e.clientX, initialStart: target.startTime, initialEnd: target.endTime, moved: false,
    };
  };
  const nudgeEdge = (clip: TimelineInterval, edge: 'start' | 'end', event: React.KeyboardEvent) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !onIntervalsChange) return;
    event.preventDefault();
    event.stopPropagation();
    const next = applyIntervalDrag({ mode: edge, initialStart: clip.startTime, initialEnd: clip.endTime,
      deltaSec: (event.key === 'ArrowLeft' ? -1 : 1) * FRAME_DURATION, fullVideoDuration });
    onSelectInterval?.(clip.id);
    onIntervalsChange(renderIntervals.map(interval => interval.id === clip.id ? { ...interval, ...next } : interval));
  };
  const beginResize = (id: number, edge: 'start' | 'end', e: React.PointerEvent) => beginIntervalGesture(id, edge, e);
  const beginMove = (id: number, e: React.PointerEvent) => beginIntervalGesture(id, isSequence ? 'reorder' : 'move', e);

  const beginTimeIndicatorDrag = (e: React.PointerEvent) => {
    if (e.button !== 0) return; // right-click belongs to the context menu
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    isDraggingTimeRef.current = true;
    timeIndicatorDragRef.current = {
      startClientX: e.clientX,
      initialTime: axisCurrentTime,
    };
    setIsSeeking(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: PointerEvent) => {
      // Handle interval dragging — keep the gesture local until pointerup.
      if (isDraggingRef.current && dragStateRef.current && onIntervalsChangeRef.current) {
        const state = dragStateRef.current;
        const deltaPx = e.clientX - state.startClientX;
        if (!state.moved && Math.abs(deltaPx) < (state.mode === 'start' || state.mode === 'end' ? 1 : 4)) return;
        state.moved = true;
        suppressClickRef.current = true;
        const deltaSec = deltaPx * state.secondsPerPixel;
        let updated: TimelineInterval[];
        if (state.mode === 'reorder') {
          updated = reorderSceneAtTime(state.original, state.intervalId, state.initialCenter + deltaSec, state.centers);
        } else {
          const nextTimes = applyIntervalDrag({
            mode: state.mode, initialStart: state.initialStart, initialEnd: state.initialEnd,
            deltaSec, fullVideoDuration,
          });
          updated = state.original.map(it => it.id === state.intervalId ? { ...it, ...nextTimes } : it);
        }
        draftIntervalsRef.current = updated === state.original ? null : updated;
        setDraftIntervals(updated);
      }

      // Handle time indicator dragging — one seek per animation frame so
      // Remotion is not asked to pause/seek on every pointer event.
      if (isDraggingTimeRef.current && timeIndicatorDragRef.current) {
        const rect = timelineRef.current?.getBoundingClientRect();
        if (!rect) return;
        const secondsPerPx = axisRef.current.duration / rect.width;
        const deltaPx = e.clientX - timeIndicatorDragRef.current.startClientX;
        const deltaSec = deltaPx * secondsPerPx;
        const axisTime = Math.max(0, Math.min(timeIndicatorDragRef.current.initialTime + deltaSec, axisRef.current.duration));
        const newTime = axisRef.current.sourceTimeAtAxis(axisTime);
        pendingTimeRef.current = newTime;
        if (timeRafRef.current == null) {
          timeRafRef.current = requestAnimationFrame(() => {
            timeRafRef.current = null;
            const nextTime = pendingTimeRef.current;
            if (nextTime == null) return;
            onTimeChangeRef.current?.(nextTime);
            onSeekRequestRef.current?.(nextTime, { scrub: true });
          });
        }
      }
    };

    const flushPendingTime = () => {
      if (timeRafRef.current != null) {
        cancelAnimationFrame(timeRafRef.current);
        timeRafRef.current = null;
      }
      const nextTime = pendingTimeRef.current;
      pendingTimeRef.current = null;
      if (nextTime != null) {
        onTimeChangeRef.current?.(nextTime);
        onSeekRequestRef.current?.(nextTime, { scrub: true });
      }
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        dragStateRef.current = null;
        const draft = draftIntervalsRef.current;
        if (draft) {
          onIntervalsChangeRef.current?.(draft);
        }
      }
      if (isDraggingTimeRef.current) {
        isDraggingTimeRef.current = false;
        timeIndicatorDragRef.current = null;
        flushPendingTime();
        // Clear seeking state after dragging ends
        clearSeekingSoon();
      }
    };

    const cancelGesture = () => {
      isDraggingRef.current = false;
      dragStateRef.current = null;
      draftIntervalsRef.current = null;
      setDraftIntervals(null);
      isDraggingTimeRef.current = false;
      timeIndicatorDragRef.current = null;
      pendingTimeRef.current = null;
      clearSeekingSoon();
    };
    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelGesture();
    };
    window.addEventListener('keydown', cancelOnEscape);
    window.addEventListener('pointermove', handleMouseMove);
    window.addEventListener('pointerup', handleMouseUp);
    window.addEventListener('pointercancel', cancelGesture);
    return () => {
      window.removeEventListener('pointermove', handleMouseMove);
      window.removeEventListener('pointerup', handleMouseUp);
      window.removeEventListener('pointercancel', cancelGesture);
      window.removeEventListener('keydown', cancelOnEscape);
      if (timeRafRef.current != null) {
        cancelAnimationFrame(timeRafRef.current);
        timeRafRef.current = null;
      }
      if (seekingTimeoutRef.current != null) {
        clearTimeout(seekingTimeoutRef.current);
        seekingTimeoutRef.current = null;
      }
    };
  }, [clearSeekingSoon, fullVideoDuration]);

  // Generate time markers
  const timeMarkers = [];
  const markerInterval = [1, 2, 5, 10, 15, 30, 60, 120, 300].find(step => step * pixelsPerSecond >= 70) ?? 600;
  for (let i = 0; i <= timelineDuration; i += markerInterval) {
    timeMarkers.push(i);
  }

  const layerCount = layers?.length ?? 0;
  // Layer lanes sit at the TOP of the timeline (directly under the ruler,
  // above the video track, like overlay tracks in a video editor). Every
  // row below the ruler shifts down by the lane area's height.
  // Sections starting at/after the clip end never render and are not drawn in
  // Full-video view: they reserve no lane row and the lane never sees them.
  const visibleEffectSections = sectionsWithinClip(effectSections ?? [], clipDuration);
  const effectRowCount = effectLaneRowCount(visibleEffectSections);
  const laneAreaHeight = layerCount * 20 + effectRowCount * EFFECT_LANE_HEIGHT;
  // Framing and keyframe transitions live inside the 64 px video blocks.
  const containerHeight = 96 + laneAreaHeight + 34;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Timeline Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {onVideoLockedChange && (
            <div className="inline-flex rounded-md border bg-background p-0.5">
              <Button variant={!isVideoLocked ? "secondary" : "ghost"} size="sm" className="h-7 px-2 text-xs"
                aria-pressed={!isVideoLocked} title={t('timeline.fullVideoHint')}
                onClick={() => onVideoLockedChange(false)}>{t('timeline.fullVideo')}</Button>
              <Button variant={isVideoLocked ? "secondary" : "ghost"} size="sm" className="h-7 px-2 text-xs"
                aria-pressed={isVideoLocked} title={t('timeline.clipSequenceHint')}
                onClick={() => onVideoLockedChange(true)}>{t('timeline.clipSequence')}</Button>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleScroll('left')}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleScroll('right')}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          {toolbarStart}
        </div>

        <div className="flex items-center gap-2">
          {(onUndo || onRedo) && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onUndo}
                disabled={!canUndo}
                className="h-8 w-8 p-0"
                title={t('timeline.undo')}
                aria-label={t('timeline.undo')}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onRedo}
                disabled={!canRedo}
                className="h-8 w-8 p-0"
                title={t('timeline.redo')}
                aria-label={t('timeline.redo')}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
              <span className="h-5 w-px bg-border mx-1" aria-hidden="true" />
            </>
          )}
          {onAddLayer && (
            <AddLayerMenu
              onAdd={onAddLayer}
              showLabel={false}
              triggerTestId="timeline-add-layer"
            />
          )}
          {onAddEffect && <EffectsLibraryButton onAdd={onAddEffect} />}
          <Popover open={addChoiceOpen} onOpenChange={setAddChoiceOpen}>
            <PopoverAnchor asChild>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAddInterval}
                className="h-8 px-2"
                title={isVideoLocked ? t('timeline.addIntervalAfterCurrent') : t('timeline.addIntervalAtCurrentTime')}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </PopoverAnchor>
            <PopoverContent align="start" className="w-64 p-1" data-testid="timeline-add-choice">
              <p className="px-2 py-1.5 text-xs text-muted-foreground">{t('timeline.playheadInsideScene')}</p>
              <button
                type="button"
                disabled={!canSplitAtPlayhead}
                onClick={() => { setAddChoiceOpen(false); handleSplitAtPlayhead(); }}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
              >
                <Scissors className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('timeline.splitSceneHere', { time: formatTime(splitTime) })}
              </button>
              <button
                type="button"
                onClick={() => { setAddChoiceOpen(false); handleAddCopy(); }}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
              >
                <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('timeline.addCopyHere')}
              </button>
            </PopoverContent>
          </Popover>
          {onIntervalsChange && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSplitAtPlayhead}
              disabled={!canSplitAtPlayhead}
              className="h-8 px-2"
              data-testid="timeline-split-scene"
              title={t('timeline.splitScene')}
              aria-label={t('timeline.splitScene')}
            >
              <Scissors className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomOut}
            className="h-8 w-8 p-0"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground min-w-[40px] text-center select-none user-select-none">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomIn}
            className="h-8 w-8 p-0"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Timeline Container */}
      <ContextMenu onOpenChange={open => { if (!open) { setContextTime(null); setContextIntervalId(null); } }}>
        <ContextMenuTrigger asChild>
          <div
            ref={scrollContainerRef}
            onContextMenu={(event) => {
              // Replays can share source times. Address the actual scene,
              // including a keyboard-opened menu on its focused block.
              const scene = event.target instanceof Element
                ? event.target.closest<HTMLElement>('[data-timeline-interval-id]')
                : null;
              setContextIntervalId(scene ? Number(scene.dataset.timelineIntervalId) : null);
              const rect = timelineRef.current?.getBoundingClientRect();
              // Keyboard context menus retain the current playhead position.
              setContextTime(rect?.width && (event.clientX || event.clientY)
                ? sourceTimeAtAxis(Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) * timelineDuration)
                : null);
            }}
            className="group/timeline relative overflow-x-auto overflow-y-hidden bg-muted rounded-lg border"
          >
        <div
          ref={timelineRef}
          data-testid="timeline-content"
          className="relative min-w-full cursor-pointer select-none"
          style={{
            width: `${timelineDuration * zoom * 10}px`,
            height: `${containerHeight}px`
          }}
          onClick={handleTimelineClick}
          onPointerDown={(e) => {
            // Clicking empty timeline space (not a scene bar / lane / handle,
            // which all stopPropagation) clears the current selection.
            // Right-click opens the context menu instead and keeps selection.
            if (e.button !== 0) return;
            if (e.target === e.currentTarget) {
              onSelectInterval?.(null);
              onLayerSelect?.(null);
            }
          }}
        >
          {/* Time markers */}
          <div className="absolute top-0 left-0 right-0 h-6 flex items-end pointer-events-none select-none">
            {timeMarkers.map((time) => (
              <div
                key={time}
                className="absolute text-xs text-muted-foreground select-none pointer-events-none"
                style={{
                  left: `${(time / timelineDuration) * 100}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                {formatTime(time)}
              </div>
            ))}
          </div>

          {/* Timeline track (increased height to fit clip indicator) */}
          <div className="absolute left-0 right-0 h-16 bg-border rounded select-none pointer-events-none" style={{ top: 32 + laneAreaHeight }}></div>

          {/* Intervals indicators */}
          {axisRanges.map(({ clip, start, end }, index) => {
            const startPercent = (start / timelineDuration) * 100;
            const endPercent = (end / timelineDuration) * 100;
            const width = Math.max(0, endPercent - startPercent);
            const isSelected = selectedIntervalId === clip.id;
            const compact = (end - start) * pixelsPerSecond < 112;
            const controlsBelow = (end - start) * pixelsPerSecond < 64;
            const hasIntervalCropKeyframes = sceneCropKeyframes?.some(
              (keyframe) => keyframe.endTime > clip.startTime && keyframe.time < clip.endTime,
            );
            return (
              <div
                key={clip.id}
                data-testid={`timeline-interval-${clip.id}`}
                data-timeline-interval-id={clip.id}
                tabIndex={0}
                role={isSequence ? 'group' : undefined}
                aria-label={isSequence ? t('timeline.reorderScene', { number: index + 1 }) : undefined}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget || !isSequence || !event.altKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                  event.preventDefault();
                  event.stopPropagation();
                  const to = index + (event.key === 'ArrowLeft' ? -1 : 1);
                  if (to < 0 || to >= renderIntervals.length) return;
                  const next = [...renderIntervals];
                  next.splice(index, 1);
                  next.splice(to, 0, clip);
                  onIntervalsChange?.(next);
                }}
                className={`group/scene absolute h-16 bg-primary rounded border border-primary flex items-center justify-center flex-col cursor-grab touch-none ${
                  controlsBelow ? 'before:absolute before:top-full before:h-10 before:w-14 before:left-1/2 before:-translate-x-1/2 before:pointer-events-none hover:before:pointer-events-auto focus-within:before:pointer-events-auto' : ''
                } ${isSelected ? 'ring-2 ring-white ring-offset-1 ring-offset-primary/40' : ''
                }`}
                style={{ left: `${startPercent}%`, width: isSequence ? `max(2px, calc(${width}% - 3px))` : `${width}%`, top: 32 + laneAreaHeight }}
                title={compact ? undefined : `${clip.title ?? 'Intervalo'}: ${formatTime(clip.startTime)} - ${formatTime(clip.endTime)}`}
                onPointerDown={(e) => { onSelectInterval?.(clip.id); beginMove(clip.id, e); }}
              >
                <TimelineScenePreviews
                  previews={visibleScenePreviews.filter((preview) => preview.intervalId === clip.id)}
                  intervalStart={clip.startTime}
                  intervalEnd={clip.endTime}
                  pixelsPerSecond={pixelsPerSecond}
                  frames={thumbnailFrames}
                  focusCrop={sceneCropKeyframes?.find(kf => kf.time <= clip.startTime && kf.endTime > clip.startTime)}
                  sourceAspectRatio={sourceAspectRatio}
                  showSeekControls={!hasIntervalCropKeyframes && !!sceneMarkers?.length}
                  formatTime={formatTime}
                  onSeek={(time) => { onSelectInterval?.(clip.id); seekTo(time); }}
                />
                {hasIntervalCropKeyframes && (
                  <CropCenterTrack
                    keyframes={sceneCropKeyframes!}
                    compact={compact}
                    controlsBelow={controlsBelow}
                    intervalStart={clip.startTime}
                    intervalEnd={clip.endTime}
                    pixelsPerSecond={pixelsPerSecond}
                    viewStart={pixelsPerSecond ? clip.startTime + scrollPosition / pixelsPerSecond - start : clip.startTime}
                    viewEnd={pixelsPerSecond ? clip.startTime + (scrollPosition + (viewportWidth || timelineWidth)) / pixelsPerSecond - start : clip.endTime}
                    currentTime={currentTime}
                    videoUrl={videoUrl}
                    sourceAspectRatio={sourceAspectRatio}
                    onChange={onCropKeyframesChange}
                    onSeekRequest={(time) => { onSelectInterval?.(clip.id); seekTo(time); }}
                    keyframeTransitions={keyframeTransitions}
                    onKeyframeTransitionChange={onKeyframeTransitionChange}
                    onApplyKeyframeTransitionToAll={onApplyKeyframeTransitionToAll}
                    onRandomizeKeyframeTransitions={onRandomizeKeyframeTransitions}
                  />
                )}
                {/* Left resize handle */}
                {onIntervalsChange && (
                  <div
                    className={`absolute z-[1] left-0 top-0 bottom-0 bg-white/70 hover:bg-white cursor-ew-resize rounded-l touch-none ${compact ? "w-1 opacity-0 group-hover/scene:opacity-100 group-focus-within/scene:opacity-100" : "w-3 sm:w-2"}`}
                    onPointerDown={(e) => beginResize(clip.id, 'start', e)}
                    onKeyDown={(e) => nudgeEdge(clip, 'start', e)}
                    tabIndex={0} role="slider" aria-orientation="horizontal"
                    aria-label={t('timeline.trimStart', { number: index + 1 })}
                    title={t('timeline.frameTrimHint')}
                    aria-valuemin={0}
                    aria-valuemax={Math.round(clip.endTime * EXPORT_FPS) - 1}
                    aria-valuenow={Math.round(clip.startTime * EXPORT_FPS)}
                    aria-valuetext={formatFrameTime(clip.startTime)}
                  />
                )}
                {/* Right resize handle */}
                {onIntervalsChange && (
                  <div
                    className={`absolute z-[1] right-0 top-0 bottom-0 bg-white/70 hover:bg-white cursor-ew-resize rounded-r touch-none ${compact ? "w-1 opacity-0 group-hover/scene:opacity-100 group-focus-within/scene:opacity-100" : "w-3 sm:w-2"}`}
                    onPointerDown={(e) => beginResize(clip.id, 'end', e)}
                    onKeyDown={(e) => nudgeEdge(clip, 'end', e)}
                    tabIndex={0} role="slider" aria-orientation="horizontal"
                    aria-label={t('timeline.trimEnd', { number: index + 1 })}
                    title={t('timeline.frameTrimHint')}
                    aria-valuemin={Math.round(clip.startTime * EXPORT_FPS) + 1}
                    aria-valuemax={Math.floor(fullVideoDuration * EXPORT_FPS)}
                    aria-valuenow={Math.round(clip.endTime * EXPORT_FPS)}
                    aria-valuetext={formatFrameTime(clip.endTime)}
                  />
                )}
                <div
                  data-testid="scene-label"
                  className={`absolute pointer-events-none select-none text-center ${compact
                    ? 'left-1/2 -top-7 z-30 hidden w-max -translate-x-1/2 group-hover/scene:block group-focus-within/scene:block'
                    : 'right-3 top-1 overflow-hidden'}`}
                  style={compact ? undefined : { left: 50 }}
                >
                  <div className={`inline-flex max-w-full ${compact ? "flex-row items-center gap-1.5" : "flex-col"} rounded bg-black/75 px-1.5 py-0.5 text-white shadow-sm`}>
                    <div className="truncate text-[11px] font-bold leading-[13px]">{t('timeline.sceneNumber', { number: index + 1 })}</div>
                    <div className="truncate text-[11px] font-medium leading-[13px] tabular-nums">
                      {formatFrameTime(clip.startTime)} - {formatFrameTime(clip.endTime)}
                    </div>
                  </div>
                </div>
                {onIntervalsChange && (() => {
                  const base = displayIntervals && displayIntervals.length > 0
                    ? displayIntervals
                    : (selectedClip ? [selectedClip] : []);
                  const canDelete = base.length > 1;

                  return canDelete ? (
                    <button
                      type="button"
                      className={`absolute ${controlsBelow ? "z-20 top-[calc(100%+6px)] left-[calc(50%+2px)] h-6 w-6" : "z-[2] -top-2 -right-2"} bg-background/90 border rounded p-1 shadow hover:bg-background pointer-events-none group-hover/scene:pointer-events-auto group-focus-within/scene:pointer-events-auto focus-visible:pointer-events-auto opacity-0 group-hover/scene:opacity-100 group-focus-within/scene:opacity-100 focus-visible:opacity-100`}
                      // Stop the pointerdown from bubbling to the bar, whose
                      // beginMove() would setPointerCapture and swallow this
                      // button's click (the original "trash does nothing" bug).
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); handleDeleteInterval(clip.id); }}
                      title={t('timelineExtra.removeInterval')}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-foreground" />
                    </button>
                  ) : null;
                })()}
              </div>
            );
          })}

          {/* Per-cut transition badges (between consecutive scene bars) */}
          {onTransitionChange && (() => {
            const base = (displayIntervals && displayIntervals.length > 0)
              ? displayIntervals
              : (selectedClip ? [selectedClip] : []);
            if (base.length < 2) return null;
            return base.slice(1).map((clip, i) => {
              const leftPercent = (axisRanges[i + 1].start / timelineDuration) * 100;
              const current = transitions?.find((tr) => tr.afterSceneIndex === i) ?? null;
              const compactCut = Math.min(axisRanges[i].end - axisRanges[i].start, axisRanges[i + 1].end - axisRanges[i + 1].start) * pixelsPerSecond < 112;
              return (
                <div
                  key={`cut-${i}`}
                  className="group/cut absolute z-40 h-6 w-6 -translate-x-1/2"
                  style={{ left: `${leftPercent}%`, top: 52 + laneAreaHeight }}
                >
                  {current && compactCut && (
                    <span aria-hidden="true" className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow ring-1 ring-black/50 group-hover/cut:opacity-0 group-focus-within/cut:opacity-0" />
                  )}
                  <div className="absolute left-1/2">
                  <TransitionBadge
                    cutIndex={i}
                    compact={compactCut}
                    transition={current}
                    onChange={(tr) => onTransitionChange(i, tr)}
                    onApplyToAll={(tr) => onApplyTransitionToAll?.(tr)}
                    onRandomize={(kinds) => onRandomizeTransitions?.(kinds)}
                    onAuto={onAutoTransitions}
                    autoDisabledReason={autoTransitionsDisabledReason}
                    autoContentTypeLabel={autoTransitionsContentTypeLabel}
                  />
                  </div>
                </div>
              );
            });
          })()}

          {/* Current time indicator */}
          <div
            className={`absolute top-6 w-0.5 z-10 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.6)] cursor-grab touch-none ${isDraggingTimeRef.current ? 'cursor-grabbing' : ''}`}
            style={{
              left: `${getCurrentTimePosition()}%`,
              height: 72 + laneAreaHeight
            }}
            onPointerDown={beginTimeIndicatorDrag}
            tabIndex={0} role="slider" aria-orientation="horizontal"
            aria-label={t('timeline.playhead')} aria-valuemin={0}
            aria-valuemax={Math.floor(timelineDuration * EXPORT_FPS)}
            aria-valuenow={Math.round(axisCurrentTime * EXPORT_FPS)}
            aria-valuetext={formatFrameTime(axisCurrentTime)}
            title={t('timeline.frameTrimHint')}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
              event.preventDefault();
              event.stopPropagation();
              const frame = Math.round(axisCurrentTime * EXPORT_FPS) + (event.key === 'ArrowLeft' ? -1 : 1);
              seekTo(sourceTimeAtAxis(Math.max(0, Math.min(frame / EXPORT_FPS, timelineDuration))));
            }}
          >
            <div
              data-edge={playheadHeadEdge}
              className={`absolute -top-2 ${
                playheadHeadEdge === 'start' ? 'left-0' : playheadHeadEdge === 'end' ? 'right-0' : '-left-2'
              } w-5 h-5 sm:w-4 sm:h-4 rounded-full border-2 border-white cursor-grab hover:scale-110 touch-none ${
                isSeeking || isDraggingTimeRef.current ? 'bg-black' : 'bg-primary'
              } ${isDraggingTimeRef.current ? 'cursor-grabbing' : ''}`}
              onPointerDown={beginTimeIndicatorDrag}
            ></div>
            <div
              data-edge={playheadLabelEdge}
              className={`absolute -bottom-8 transform translate-y-[-50%] ${
                playheadLabelEdge === 'start' ? 'left-0' : playheadLabelEdge === 'end' ? 'right-0' : '-right-0 translate-x-1/2'
              } bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none select-none user-select-none`}
            >
              {formatFrameTime(axisCurrentTime)}
            </div>
          </div>

          {/* Layer lanes (source-video coordinates, same axis as the ruler) */}
          {(layers?.length ?? 0) > 0 && (
            <LayerLanes
              layers={layers!}
              activeLayerId={activeLayerId ?? null}
              intervals={displayIntervals}
              pxPerSecond={pixelsPerSecond}
              sequenceMode={isSequence}
              laneTop={26}
              clipDuration={clipDuration ?? 0}
              onSelect={(id) => onLayerSelect?.(id)}
              onPatch={(id, patch) => onLayerPatch?.(id, patch)}
              onReorder={onLayerReorder}
              currentTime={currentTime}
              onSeek={seekTo}
            />
          )}
          {/* Effect section lanes, below the layer lanes */}
          {visibleEffectSections.length > 0 && (
            <EffectLanes
              sections={visibleEffectSections}
              activeSectionId={activeEffectSectionId ?? null}
              intervals={displayIntervals}
              pxPerSecond={pixelsPerSecond}
              sequenceMode={isSequence}
              laneTop={26 + layerCount * 20}
              clipDuration={clipDuration ?? 0}
              onSelect={(id) => onEffectSectionSelect?.(id)}
              onCommit={(id, w) => onEffectSectionCommit?.(id, w)}
              currentTime={currentTime}
              onSeek={seekTo}
            />
          )}
        </div>
      </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-60">
          <ContextMenuItem
            disabled={!canSplitAtPlayhead}
            onSelect={handleSplitAtPlayhead}
          >
            <Scissors className="mr-2 h-3.5 w-3.5" />
            {t('timeline.split', { time: formatTime(splitTime) })}
          </ContextMenuItem>
          {onIntervalsChange && (playheadInsideScene ? (
            <ContextMenuItem
              data-testid="context-add-interval"
              onSelect={handleAddCopy}
            >
              <Copy className="mr-2 h-3.5 w-3.5" />
              {t('timeline.addCopyHere')}
            </ContextMenuItem>
          ) : (
            <ContextMenuItem
              data-testid="context-add-interval"
              onSelect={handleAddInterval}
            >
              <Plus className="mr-2 h-3.5 w-3.5" />
              {isVideoLocked
                ? t('timeline.addIntervalAfterCurrent')
                : t('timeline.addIntervalAtCurrentTime')}
            </ContextMenuItem>
          ))}
          {onIntervalsChange && contextIntervalIndex >= 0 && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                disabled={contextIntervalIndex === 0}
                onSelect={() => moveContextScene('start')}
              >
                <ArrowLeftToLine className="mr-2 h-3.5 w-3.5" />
                {t('timeline.moveSceneToStart')}
              </ContextMenuItem>
              <ContextMenuItem
                disabled={contextIntervalIndex === renderIntervals.length - 1}
                onSelect={() => moveContextScene('end')}
              >
                <ArrowRightToLine className="mr-2 h-3.5 w-3.5" />
                {t('timeline.moveSceneToEnd')}
              </ContextMenuItem>
            </>
          )}
          {onAddLayer && (
            <>
              <ContextMenuSeparator />
              <AddLayerContextMenuRows onAdd={onAddLayer} />
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* The selected layer's control bar now floats over the preview
          container — see LayerExpansionRow in ProjectEditor. */}

      {/* Zoom slider */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground select-none">
          <span className="select-none user-select-none">{t('timeline.zoomLabel')}</span>
          <span className="select-none user-select-none">{Math.round(zoom * 100)}%</span>
        </div>
        <Slider
          value={[zoom]}
          onValueChange={(value) => setZoom(value[0])}
          min={0.1}
          max={10}
          step={0.1}
          className="w-full"
        />
      </div>
    </div>
  );
};

export default Timeline;
